import React, { FunctionComponent, useState, SyntheticEvent } from 'react';
import { prop, omit, merge, isNil, path, map, values } from 'ramda';
import { Grid } from 'semantic-ui-react';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { Location } from 'history';
import queryString from 'query-string';
import withStyles from 'react-jss';
import moment from 'moment';
import { FormattedNumber } from 'react-intl';
import {
  FlexibleWidthXYPlot,
  HorizontalGridLines,
  XAxis,
  YAxis,
  LineSeries,
  Crosshair,
} from 'react-vis';

import { theme } from 'config';
import { ClassProp } from 'helpers/types';
import ReportBase, { ReportParams } from 'scenes/Report/components/ReportBase';
import Query from 'scenes/Report/components/Query';
import MetricHeader from 'scenes/Report/components/MetricHeader';
import FormSelect from 'components/FormSelect';
import { Timeslice } from 'scenes/Report/Report.helper';

import { tickFormatXAxis, groupByCampaign } from '../ThirdPartyReports.helper';
import { GET_VISITOR_SHARE } from './SafegraphVisitorShare.queries';
import styles from './SafegraphVisitorShare.styles';
import {
  getX,
  tickFormatYAxis,
  timesliceOptions,
  getVisitorShareMetrics,
} from './SafegraphVisitorShare.helpers';

export type CampaignPeriod = 'Pre-Campaign' | 'Campaign' | 'Post-Campaign';

type Styles = ClassProp<typeof styles>;

export type VisitorShareEvents = {
  transactionDate: Date;
  visitorShare: number;
};

export type VisitorShareMetrics = {
  campaignPeriod: CampaignPeriod;
  lift: number;
  avgVisitorShare: number;
};

export type VisitorShare = {
  events: ReadonlyArray<VisitorShareEvents>;
  metrics: ReadonlyArray<VisitorShareMetrics>;
};

type QueryResponse = {
  safegraphVisitorShare: VisitorShare;
};

type Props = {
  classes: Styles;
  location: Location;
  height?: number;
};

const { violet, seafoamGreen, softGray, darkGray } = theme;

const renderMetric = ({
  campaignPeriod,
  lift,
  avgVisitorShare,
}: VisitorShareMetrics) =>
  isNil(campaignPeriod) ? null : (
    <MetricHeader
      lift={lift}
      campaignPeriod={campaignPeriod}
      averageValue={
        <FormattedNumber
          maximumFractionDigits={2}
          style="percent"
          value={avgVisitorShare / 100}
        />
      }
    />
  );

const renderCrosshair = (date = '') => (
  <Crosshair
    style={{
      line: { background: darkGray },
      box: { display: 'none' },
    }}
    values={[
      {
        transactionDate: moment(date).format('YYYY/MM/DD'),
      },
    ]}
  />
);

const renderAverageLines = compose(
  values,
  map((averageLineData: any) => (
    <LineSeries
      color={theme.coral}
      strokeDasharray="7, 3"
      strokeWidth={1}
      data={averageLineData}
    />
  )),
);

const renderReport = (
  { classes, height = 450 }: Props,
  params: ReportParams,
  [timeslice, setTimeslice]: [
    Timeslice,
    React.Dispatch<React.SetStateAction<Timeslice>>,
  ],
) => ({ safegraphVisitorShare }: QueryResponse) => {
  const { metrics, events } = safegraphVisitorShare;
  const [preCampaignMetrics, campaignMetrics, postCampaignMetrics] = metrics;

  const groupedEvents = groupByCampaign(events);
  const groupedMetrics = groupByCampaign(metrics);
  const visitorShareMetrics = getVisitorShareMetrics(groupedMetrics)(events);

  const onChange = (
    _: SyntheticEvent<HTMLElement, Event>,
    { value }: { value: Timeslice },
  ) => {
    setTimeslice(value);
  };

  const DownloadHeader = (
    <Grid.Column textAlign="right">
      <FormSelect
        name="filter"
        value={timeslice}
        placeholder="View"
        label="Filter Options: "
        options={timesliceOptions}
        onChange={onChange}
      />
    </Grid.Column>
  );

  return (
    <ReportBase
      batchKey="safegraphVisitorShare"
      header="Visitor Share"
      params={params}
      downloadHeader={DownloadHeader}
    >
      <Grid.Row centered>
        <Grid className={classes.metrics}>
          <Grid.Row columns="equal" centered>
            {preCampaignMetrics && renderMetric(preCampaignMetrics)}
            {campaignMetrics && renderMetric(campaignMetrics)}
            {postCampaignMetrics && renderMetric(postCampaignMetrics)}
          </Grid.Row>
        </Grid>

        <Grid.Column className={classes.chart} textAlign="center" width={14}>
          <FlexibleWidthXYPlot
            getX={getX}
            getY={prop('visitorShare')}
            xType="time"
            height={height}
          >
            <HorizontalGridLines />
            <YAxis tickFormat={tickFormatYAxis} />
            <XAxis position="middle" tickFormat={tickFormatXAxis} />

            <LineSeries color={softGray} data={groupedEvents['Pre-Campaign']} />
            <LineSeries color={violet} data={groupedEvents['Campaign']} />
            <LineSeries
              color={seafoamGreen}
              data={groupedEvents['Post-Campaign']}
            />

            {renderAverageLines(visitorShareMetrics)}

            {campaignMetrics &&
              renderCrosshair(
                path(['Campaign', 0, 'transactionDate'], groupedEvents),
              )}

            {postCampaignMetrics &&
              renderCrosshair(
                path(['Post-Campaign', 0, 'transactionDate'], groupedEvents),
              )}
          </FlexibleWidthXYPlot>
        </Grid.Column>
      </Grid.Row>
    </ReportBase>
  );
};

const SafegraphVisitorShare: FunctionComponent<Props> = ({
  location,
  ...rest
}) => {
  let params = queryString.parse(location.search) as ReportParams;
  const [timeslice, setTimeslice] = useState<Timeslice>(Timeslice.WEEKLY);
  params = merge(params, { timeslice });

  return (
    <Query
      query={GET_VISITOR_SHARE}
      queryKey="safegraphVisitorShare"
      reportName="Visitor Share"
      batchKey="safegraphVisitorShare"
      variables={{ params: omit(['merchantName'], params) }}
      render={renderReport(rest as Props, params, [timeslice, setTimeslice])}
    />
  );
};

export default compose<Props, any>(
  withStyles(styles),
  withRouter,
)(SafegraphVisitorShare);
